import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mediaApi } from '@/api/mediaWrapper';
import ChangePhotoModal from '@/components/media/ChangePhotoModal';
import ImageEnlargeOverlay from '@/components/shared/ImageEnlargeOverlay';
import Maximize2 from '@/icons/Maximize2';
import Pencil from '@/icons/Pencil';
import Upload from '@/icons/Upload';

import './profile-photo-upload.css';

export type PhotoDisplayStatus = 'safe' | 'processing' | 'unavailable' | null;

export interface ProfilePhotoUploadProps {
  familyId: string;
  familyMemberId: string;
  canModify: boolean;
  photoUrl?: string | null;
  /** When media exists but is not safe to serve: 'processing' (pending scan/mod) or 'unavailable' (infected/rejected). */
  photoDisplayStatus?: PhotoDisplayStatus;
  onUploadSuccess?: () => void;
  variant?: 'profile' | 'edit';
  /** When set and no photo is shown, display editorial initials instead of the default placeholder. */
  placeholderInitials?: string;
}

const ProfilePhotoUpload: React.FC<ProfilePhotoUploadProps> = ({
  familyId,
  familyMemberId,
  canModify,
  photoUrl,
  photoDisplayStatus = null,
  onUploadSuccess,
  variant = 'profile',
  placeholderInitials
}) => {
  const { t } = useTranslation('forms');
  const { t: tScreens } = useTranslation('screens');
  const [showModal, setShowModal] = useState(false);
  const [showEnlargeOverlay, setShowEnlargeOverlay] = useState(false);

  const block = 'profile-photo-upload';
  const modifier = variant === 'edit' ? `${block}--edit` : `${block}--profile`;
  const showImage = photoUrl && photoDisplayStatus === 'safe';
  const statusMessage =
    photoDisplayStatus === 'processing'
      ? tScreens('mediaViewer.processing')
      : photoDisplayStatus === 'unavailable'
        ? tScreens('mediaViewer.unavailable')
        : null;

  const handleSelect = async (mediaAssetId: string) => {
    const result = await mediaApi.setPrimaryProfilePhoto(familyId, familyMemberId, mediaAssetId);
    if (result.success) {
      onUploadSuccess?.();
    }
  };

  const showEnlargeAction = variant === 'profile' && showImage;
  const showChangeAction = canModify;
  const showActions = showEnlargeAction || showChangeAction;
  const changeLabel = showImage ? t('person.changePhoto') : t('person.uploadPhoto');

  return (
    <div className={`${block} ${modifier}`.trim()}>
      <div className={`${block}__image-area`}>
        {showImage ? (
          <div className={`${block}__photo-wrap`}>
            <img src={photoUrl!} alt={tScreens('mediaViewer.profileAlt')} className={`${block}__image`} />
          </div>
        ) : placeholderInitials && !statusMessage ? (
          <div className={`${block}__placeholder ${block}__placeholder--initials`}>
            <span className={`${block}__initials`} aria-hidden>
              {placeholderInitials}
            </span>
          </div>
        ) : (
          <div
            className={statusMessage ? `${block}__placeholder ${block}__placeholder--status` : `${block}__placeholder`}
          >
            <span className={`${block}__placeholder-icon`} aria-hidden>
              {statusMessage ? (photoDisplayStatus === 'processing' ? '⏳' : '🚫') : '📷'}
            </span>
            <span className={`${block}__placeholder-text`}>{statusMessage ?? t('person.profilePhoto')}</span>
          </div>
        )}

        {showActions && (
          <div className={`${block}__actions`}>
            {showEnlargeAction && (
              <button
                type="button"
                className="profile-entry-icon-btn"
                onClick={() => setShowEnlargeOverlay(true)}
                aria-label={t('person.viewFullSizeAria')}
                title={t('person.viewFullSize')}
              >
                <Maximize2 width={14} height={14} />
              </button>
            )}
            {showChangeAction && (
              <button
                type="button"
                className="profile-entry-icon-btn"
                onClick={() => setShowModal(true)}
                aria-label={changeLabel}
                title={changeLabel}
              >
                {showImage ? <Pencil width={14} height={14} /> : <Upload width={14} height={14} />}
              </button>
            )}
          </div>
        )}
      </div>

      {showModal && (
        <ChangePhotoModal
          familyId={familyId}
          familyMemberId={familyMemberId}
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSelect={handleSelect}
          onUploadSuccess={onUploadSuccess}
        />
      )}

      {showEnlargeOverlay && (
        <ImageEnlargeOverlay
          isOpen={showEnlargeOverlay}
          onClose={() => setShowEnlargeOverlay(false)}
          familyId={familyId}
          familyMemberId={familyMemberId}
        />
      )}
    </div>
  );
};

export default ProfilePhotoUpload;
